import imageSegmentationApi from '@frontend/services/api/image-segmentation';
import { imageSegmentationEventBroker, IMAGE_SEGMENTATION_EVENTS } from '@frontend/group/modules/image-segmentation/event-broker';
import { normalizeBrowserUrl } from '@frontend/store/creative-studio/chat-function-handlers/pdp-generation-paths';
import { resolveImageEditSource } from '@frontend/store/creative-studio/image-edit';
import { getSelectedResizeSizes } from '@frontend/store/creative-studio';
import { mergeResizeSizes, normalizeResizeHints } from '@frontend/store/creative-studio/resize-sizes';
import { leaveAiStudio } from '@frontend/store/creative-studio/ai-studio-sessions-bridge';
import { POST_ACTION_RESIZE_DATA_KEY } from '@frontend/constants/post-actions';

export async function handleImportBannerImage($message, { state, dispatch } = {}) {
  let args;
  try {
    const raw = $message.function_call.arguments;
    args = (typeof raw === 'string' ? JSON.parse(raw) : raw) || {};
  } catch {
    args = {};
  }

  const lastUserMessage = [...(state?.messages || [])].reverse().find(message => message?.role === 'user');
  const source = resolveImageEditSource({
    imageIndex: Number.isInteger(args.image_index) ? args.image_index : null,
    savedAttachments: $message.savedAttachments || [],
    userAttachments: lastUserMessage?.attachments || [],
    messages: state?.messages || [],
  });

  if (!source) {
    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content: 'Please attach the banner image you want to import and resize.',
    });
    return;
  }

  const selectedSizes = [
    ...getSelectedResizeSizes(state.allNetworks || [], state.selectedSizeIds || new Set()),
    ...(state.selectedTemplateSizes || []),
  ];
  const sizes = mergeResizeSizes(args.target_sizes, selectedSizes);
  const resizeData = sizes.length ? {
    sizes,
    ...normalizeResizeHints(args, lastUserMessage?.content || ''),
    createNewLayout: args.create_new_layout === true,
  } : null;

  try {
    const imagePath = normalizeBrowserUrl(source.url);
    const layoutName = source.name ? `${source.name.replace(/\.[^.]+$/, '')} Layout` : 'AI Studio Image Layout';

    if (args.create_layers !== true) {
      const { data } = await axios.post('/banner/0/group/create-layout-from-single-image', {
        product_name: layoutName,
        image_url: imagePath,
        use_image_size: 1,
        skip_version: 1,
      });
      if (!data?.success || !data?.redirect_url) {
        throw new Error(data?.message || 'Could not create a layout from the image.');
      }

      const redirectUrl = new URL(data.redirect_url, window.location.origin);
      if (resizeData) {
        localStorage.setItem(POST_ACTION_RESIZE_DATA_KEY, JSON.stringify(resizeData));
        redirectUrl.searchParams.append('post-actions', 'resize');
      }
      leaveAiStudio(redirectUrl.toString());
      return;
    }

    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content: 'Analyzing the image. Review the layers before creating the layout.',
    });

    const { data } = await imageSegmentationApi.startSegmentation(0, imagePath);
    if (data?.success === false || !data?.job_uuid) {
      throw new Error(data?.message || 'Could not start image analysis.');
    }

    imageSegmentationEventBroker.fire(IMAGE_SEGMENTATION_EVENTS.SHOW_PREVIEW, {
      jobUuid: data.job_uuid,
      customerId: 0,
      imagePath,
      originalImageUrl: imagePath,
      layoutId: null,
      layoutName,
      postActionResize: resizeData,
    });
  } catch (error) {
    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content: error?.response?.data?.message || error.message || 'Could not import the image. Please try again.',
    });
  }
}
